import { observable, action, Lambda, computed, observe } from "mobx";
import { DeviceDetect, eBranches } from "sb-resp-lib";
import { SuperBannerItem, SiteDirection, CalcsProps, PaginationItemProps, SelectionType, Selection, PickedEvent, MasterEvent, Market, SuperBannerCMSConfiguration, ItemState, GameStatus } from "../../../data";
import { DataParser } from "../../../parser";
import { OddsFormatHelper } from "../helpers";

// TODO
// slider step = card width + margin
const defaultStep = 304;

export class SuperBannerData {
    @observable currentSelectedSportId: number;
    @observable items: SuperBannerItem[];
    oddsFormatter: OddsFormatHelper;
    calcs: CalcsProps;
    currentPage: number;
    @observable pages: PaginationItemProps[];
    @observable itemWidth: number | undefined;
    deviceDetect: DeviceDetect;
    disposeItemsChange: Lambda;
    disposeCurrentSelectedSportIdChange: Lambda;

    get siteDirection(): SiteDirection {
        if (document.documentElement) {
            /* istanbul ignore next */
            return document.documentElement.getAttribute("dir") === "rtl"
                ? SiteDirection.rightToLeft
                : SiteDirection.leftToRight;
        }

        return SiteDirection.leftToRight;
    }

    @observable provider: DataParser;

    disposeList: Lambda[];
    private statusesToDisplay: ItemState[];

    constructor(provider: DataParser, oddsFormatHelper: OddsFormatHelper) {
        this.provider = provider;
        this.oddsFormatter = oddsFormatHelper;
        this.items = [];
        this.disposeList = [];

        this.items = this.provider.items;
        //this.reinitObserves();

        this.calcs = {
            step: defaultStep,
            container: 0,
            wrapper: 0,
            multiplier: 1
        };
        this.pages = [];
        this.currentPage = 0;
        this.itemWidth = undefined;
        this.deviceDetect = provider.dataFacade.deviceDetectHelper.deviceDetect;

        this.statusesToDisplay = [ItemState.available, ItemState.loading];
        this.currentSelectedSportId = provider.currentSelectedSportId;
        this.disposeItemsChange = observe(this.provider, (change) => {
            let changedObject = change.object as DataParser;
            this.updateCollection(changedObject);
        });
        this.disposeCurrentSelectedSportIdChange = observe(this.provider, "currentSelectedSportId", (change) => {
            this.updateCurrentSelectedSportId(change.newValue);
        })
    }

    @action updateCollection(change: DataParser): void {
        this.items = change.items;
    }

    @action.bound updateCurrentSelectedSportId(sportId: number): void {
        this.currentSelectedSportId = sportId;
    }

    @computed get itemsToDisplay() {

        return this.items.filter(item =>
            this.validateStatus(item) &&
            !(this.provider.dataFacade.cms.DisplayMobileMode && !item.PickedEvent)
            && this.isItemForCurrentSport(item)
            && this.isNotLiveRacingEvent(item));
    }

    validateStatus(item: SuperBannerItem): boolean {
        return this.statusesToDisplay.indexOf(item.state) !== -1 &&
            (!item.PickedEvent || this.statusesToDisplay.indexOf(item.PickedEvent.state) !== -1);
    }
    isItemForCurrentSport(item: SuperBannerItem): boolean {
        return (this.currentSelectedSportId === item.SportId || item.SportId === 0);
    }

    isNotLiveRacingEvent(item: SuperBannerItem): boolean {
        if (item.PickedEvent
            && item.PickedEvent.event
            && item.PickedEvent.event.status === GameStatus.RaceOff
            && (item.PickedEvent.event.sportID === eBranches.HorseRacing.toString()
                || item.PickedEvent.event.sportID === eBranches.GreyHoundsRacing.toString())) {
            return false;
        }
        return true;
    }

    /*
    private iterate<T>(entries: T[], action: Action<T>): void {
        for (let index: number = 0; index < entries.length; index++) {
            action.call(this, entries[index]);
        }
    }
    @Debounce(100)
    observerHandle(change: any): void {
        console["log"]("the observer was notified about a change!");
        this.updateItems();
        this.reinitObserves();
    }

    // DON'T TOUCH, IT'S A BLACK MAGIC FOR OBSERVING NESTED OBSERVABLES
    reinitObserves(): void {
        this.iterate(this.disposeList, dispose => dispose());

        this.iterate(this.provider.items, item => {
            if (item.PickedEvent) {
                this.disposeList.push(observe(item.PickedEvent, this.observerHandle.bind(this)));

                if (item.PickedEvent.event) {
                    this.disposeList.push(observe(item.PickedEvent.event, this.observerHandle.bind(this)));
                }

                if (item.PickedEvent.market) {
                    this.disposeList.push(observe(item.PickedEvent.market, this.observerHandle.bind(this)));
                    this.disposeList.push(observe(item.PickedEvent.market.selections, this.observerHandle.bind(this)));
                    this.iterate(item.PickedEvent.market.selections, selection => {
                        this.disposeList.push(observe(selection, this.observerHandle.bind(this)));
                    });
                }
            }
        });
    }
    */

    // TODO Cover with tests
    /* istanbul ignore next */
    setupCalcs(wrapper: HTMLElement | null, container: HTMLElement | null, displayMultipleItems: boolean) {
        if (wrapper && container) {
            let items = container.getElementsByClassName("rj-superbanner__item");
            this.calcs.wrapper = wrapper.offsetWidth;
            !displayMultipleItems && this.setItemWidth(this.calcs.wrapper);
            this.calcs.container = container.offsetWidth;
            if (items.length) {
                let i = items[0]["offsetWidth"];
                let dI = i * items.length;
                let dC = (this.calcs.container - dI) / (items.length - 1);
                this.calcs.step = i + dC;
            }
        }
    }

    /* istanbul ignore next */
    @action.bound
    updatePagination() {
        let counter = this.getPagesCount(this.calcs);
        this.pages.length = counter;
        for (let i = 0; i < counter; i += 1) {
            this.pages[i] = {
                id: i,
                isCurrent: this.currentPage === i
            };
        }
    }

    /* istanbul ignore next */
    @action.bound
    updateCurrentPage(index: number) {
        this.pages = this.pages.map((page, i) => ({
            ...page,
            isCurrent: index === i
        }));
        this.currentPage = index;
        this.setSlideMultiplier(1);
    }

    /* istanbul ignore next */
    setCurrentPage(inc: number) {
        this.currentPage = Math.max(Math.min(this.currentPage + inc, this.pages.length - 1), 0);
    }

    /* istanbul ignore next */
    setSlideMultiplier(val: number) {
        this.calcs.multiplier = val;
    }

    /* istanbul ignore next */
    getInvisibleSpace(calcs: CalcsProps) {
        let invisibleSpace: number = 0;
        if (calcs && calcs.wrapper && calcs.container) {
            invisibleSpace = calcs.container - calcs.wrapper;
        }
        return Math.max(invisibleSpace, 0);
    }

    /* istanbul ignore next */
    getPagesCount(calcs: CalcsProps) {
        let counter = 0;
        if (calcs && calcs.step) {
            counter = Math.ceil(this.getInvisibleSpace(calcs) / calcs.step) + 1;
        }
        return counter;
    }

    /* istanbul ignore next */
    getProgressPageIndex(calcs: CalcsProps, progress: number) {
        return Math.max(
            Math.min(
                Math.round(-progress / calcs.step),
                this.getPagesCount(calcs) - 1),
            0);
    }

    /* istanbul ignore next */
    addToSlipTranspiler(selectionId: string, eventId: string): void {
        this.provider.addToSlipTranspiler(selectionId, eventId);
    }

    /* istanbul ignore next */
    getImageHeight(item: SuperBannerItem, cms: SuperBannerCMSConfiguration): string {
        const event: PickedEvent | undefined = item.PickedEvent;

        if (!cms.DisplayMultipleItems && this.provider.dataFacade.deviceDetectHelper.deviceDetect.isDesktop) {

            return "220px";
        }

        if (event) {
            if (this.hasResolvedQAMarket(event) || this.hasPickedCMSMarketAndNotResolvedYetMarket(event)) {
                return "0px";
            }

            return "80px";
        }

        return "190px";
    }

    /* istanbul ignore next */
    private hasPickedCMSMarketAndNotResolvedYetMarket(event: PickedEvent): boolean {
        /* istanbul ignore next */
        return !event.market && event.IsOutright || event.IsQA;
    }

    /* istanbul ignore next */
    private hasResolvedQAMarket(event: PickedEvent): boolean {
        /* istanbul ignore next */
        return !!event.market && event.market.type === SelectionType.QA;
    }

    /* istanbul ignore next */
    getSuperBannerItemModifier(item: SuperBannerItem): string {
        if (item.PickedEvent
            && item.PickedEvent.market
            && item.PickedEvent.market.type === SelectionType.QA) {
            return this.oddsFormatter.Outright.getOddsOrderClass(item.PickedEvent.market.selections);
        }

        return "";
    }

    /* istanbul ignore next */
    showFreeHtml(item: SuperBannerItem): boolean {
        if (item.PickedEvent && item.PickedEvent.market && item.PickedEvent.market.type === SelectionType.QA) {
            return false;
        }

        return true;
    }

    /* istanbul ignore next */
    getBetBtnModifier(selection: Selection, type: SelectionType): string {
        const betBtnModifier = `rj-superbanner__bet-button UniqueOdd_${selection.sbtechId}_${type === SelectionType.QA ? 1 : 0}`;
        if (selection.isSelected) {
            return `${betBtnModifier} rj-superbanner__bet-button--selected chosenBet`;
        }

        return betBtnModifier;
    }

    /* istanbul ignore next */
    isSelectionInBetSlip(event: MasterEvent, market: Market, selection: Selection): boolean {
        return this.provider.isSelectionInBetSlip(event, market, selection);
    }

    @action.bound
    setItemWidth(w: number) {
        this.itemWidth = w;
    }

    eventHasStarted(ev: MasterEvent): boolean {
        return ev.isLive && ev.liveStartCountdown <= 0;
    }

    eventHasLiveScb(ev: MasterEvent): boolean {
        return !!ev.scoreboard && ev.isLive;
    }
}
